/**
 * Workflow events — the discrete domain events that EVENT-kind triggers
 * subscribe to. Section 3 of the design doc enumerates them; this file
 * is the typed wire shape the worker dispatches and the trigger config
 * matches against.
 *
 * `WorkflowTrigger.eventType` (a TEXT column) stores the string value
 * of the discriminator below; the rest of the payload travels in a JSON
 * blob that the event bus consumer parses with these types.
 */

// ---------------------------------------------------------------------------
// Event type catalog
// ---------------------------------------------------------------------------

export const WORKFLOW_EVENT_TYPES = [
  'loan.status_changed',
  'loan.assigned_contact_changed',
  'loan.condition_added',
  'loan.docs_received',
  'sync.completed',
] as const;

export type WorkflowEventType = (typeof WORKFLOW_EVENT_TYPES)[number];

// ---------------------------------------------------------------------------
// Event payloads (discriminated union by `type`)
// ---------------------------------------------------------------------------

export interface LoanStatusChangedEvent {
  type: 'loan.status_changed';
  loanId: string;
  organizationId: string;
  previousStatus: string | null;
  newStatus: string;
  occurredAt: string; // ISO datetime
}

export interface LoanAssignedContactChangedEvent {
  type: 'loan.assigned_contact_changed';
  loanId: string;
  organizationId: string;
  /** Role on the loan whose contact changed, e.g. "mlo" | "processor". */
  role: string;
  previousContactId: string | null;
  newContactId: string | null;
  occurredAt: string;
}

export interface LoanConditionAddedEvent {
  type: 'loan.condition_added';
  loanId: string;
  organizationId: string;
  conditionId: string;
  conditionTitle: string;
  occurredAt: string;
}

export interface LoanDocsReceivedEvent {
  type: 'loan.docs_received';
  loanId: string;
  organizationId: string;
  /** Which doc bucket (initial_disclosures, cd, post_close, ...). */
  bucket: string;
  occurredAt: string;
}

export interface SyncCompletedEvent {
  type: 'sync.completed';
  organizationId: string;
  syncJobId: string;
  /** Loan IDs whose fields actually changed in this sync — feeds the
   *  evaluator's change-set narrowing (C5). */
  changedLoanIds: string[];
  occurredAt: string;
}

export type WorkflowEvent =
  | LoanStatusChangedEvent
  | LoanAssignedContactChangedEvent
  | LoanConditionAddedEvent
  | LoanDocsReceivedEvent
  | SyncCompletedEvent;

// ---------------------------------------------------------------------------
// Type guards (handy for the worker event router)
// ---------------------------------------------------------------------------

export function isLoanScopedEvent(
  event: WorkflowEvent,
): event is Exclude<WorkflowEvent, SyncCompletedEvent> {
  return event.type !== 'sync.completed';
}

export function isWorkflowEventType(value: string): value is WorkflowEventType {
  return (WORKFLOW_EVENT_TYPES as readonly string[]).includes(value);
}
